// WaveManager.js
// Manages the wave-based zombie spawning system

/**
 * WaveManager - Controls the wave-based spawning of zombies
 * - Tracks current wave
 * - Manages timing between waves (intermission)
 * - Calls zombieManager.spawnWave(wave)
 * - Provides wave state information
 */
export class WaveManager {
    constructor() {
        this.currentWave = 0;
        this.isIntermission = true;
        this.intermissionDuration = 5; // seconds between waves
        this.waveDurationTimer = 0; // UI hook for countdown display
        this.isWaveActive = false; // Track if a wave is currently active
        this.waveStartTime = 0; // When the current wave started
        this.totalZombiesInWave = 0; // Total zombies in current wave
        this.zombiesKilled = 0; // Zombies killed in current wave
        this.requiredKillsForWave = 0; // Required kills to complete current wave
        
        // Callbacks
        this.onWaveChange = null; // Callback for UI updates
        this.onIntermissionStart = null; // Callback for intermission started
        this.onWaveComplete = null; // Callback for wave completed
        
        // Config for wave scaling
        this.waveConfig = {
            baseZombiesPerWave: 5, // Starting number of zombies
            baseZombieScaling: 2, // Increase per wave
            runnerUnlockWave: 2, // Wave when runners appear
            runnerZombieScaling: 0.5, // Runners per wave after unlock
            bossUnlockWave: 5, // Wave when bosses appear
            bossZombieScaling: 0.2, // Bosses per wave after unlock
            maxZombiesBase: 10, // Starting max concurrent zombies
            maxZombiesScaling: 3, // Increase in max zombies per wave
            maxZombiesCap: 100, // Global cap on zombies
            spawnMultiplier: 1.5 // Spawn 50% more zombies than required to kill
        };
        
        console.log('[WaveManager] Initialized');
    }
    
    /**
     * Start the wave system
     * @param {Function} onWaveChangeCallback - Called when wave changes
     */
    start(onWaveChangeCallback = null) {
        if (onWaveChangeCallback) {
            this.onWaveChange = onWaveChangeCallback;
        }
        
        this.currentWave = 0;
        // Set isIntermission to false initially to prevent the update loop's timer
        this.isIntermission = false; 
        // Keep waveDurationTimer at 0, it's not used for the initial delay anymore
        this.waveDurationTimer = 0; 

        // Don't call onWaveChange here for wave 0

        // Start the game sequence after a short initial delay (e.g., 10 seconds)
        console.log('[WaveManager] Initializing game start sequence...');
        setTimeout(() => {
            // Trigger the countdown sequence in game.js
            if (window.startGameCountdown) {
                 window.startGameCountdown(() => {
                    // This callback is executed after the countdown finishes
                    this.startNextWave(); 
                 });
            } else {
                console.error("startGameCountdown function not found in game.js. Starting wave 1 immediately.");
                this.startNextWave(); // Fallback if the function doesn't exist
            }
        }, 10000); // 10-second initial delay before countdown starts
    }
    
    /**
     * Start the next wave
     */
    startNextWave() {
        this.currentWave++;
        this.isIntermission = false;
        this.isWaveActive = true;
        this.waveStartTime = performance.now();
        this.zombiesKilled = 0;
        
        // Calculate total zombies to spawn and required kills
        this.totalZombiesInWave = this.calculateTotalZombiesForWave(this.currentWave);
        this.requiredKillsForWave = this.calculateRequiredKillsForWave(this.currentWave);
        
        // Notify of wave change
        if (this.onWaveChange) {
            this.onWaveChange(this.currentWave, this.isIntermission);
        }
        
        // Spawn wave zombies
        if (window.zombieManager) {
            const maxZombies = this.getMaxConcurrentZombies();
            window.zombieManager.maxZombies = maxZombies; // Update max zombies allowed
            
            // Get wave configuration for zombie types
            const waveZombies = this.getZombieCountsForWave(this.currentWave);
            
            // Spawn this wave
            console.log(`[WaveManager] Starting Wave ${this.currentWave}`);
            console.log(`[WaveManager] Spawning ${this.totalZombiesInWave} zombies (need to kill ${this.requiredKillsForWave})`);
            console.log(`[WaveManager] Base: ${waveZombies.base}, Runners: ${waveZombies.runners}, Bosses: ${waveZombies.bosses}`);
            
            // Only spawn if zombieManager has the spawnWave method
            if (window.zombieManager.spawnWave) {
                window.zombieManager.spawnWave(this.currentWave, waveZombies);
            } else {
                console.error('[WaveManager] zombieManager.spawnWave method not found');
            }
        } else {
            console.error('[WaveManager] zombieManager not found');
        }
    }
    
    /**
     * Handle a zombie being killed
     */
    zombieKilled() {
        this.zombiesKilled++;
        
        // Check if wave is complete (only need to kill the required amount)
        if (this.isWaveActive && this.zombiesKilled >= this.requiredKillsForWave) {
            this.completeWave();
        }
    }
    
    /**
     * Complete the current wave and start intermission
     */
    completeWave() {
        if (!this.isWaveActive) return;
        
        this.isWaveActive = false;
        this.isIntermission = true;
        this.waveDurationTimer = this.intermissionDuration;
        
        console.log(`[WaveManager] Wave ${this.currentWave} complete`);
        
        // Kill all remaining zombies
        if (window.zombieManager) {
            console.log(`[WaveManager] Killing ${window.zombieManager.zombies.length} remaining zombies`);
            window.zombieManager.zombies.forEach(zombie => {
                window.zombieManager.killZombie(zombie, true); // Pass true for wave completion kills
            });
        }
        
        // Callback for wave complete
        if (this.onWaveComplete) {
            this.onWaveComplete(this.currentWave);
        }
        
        // Callback for intermission
        if (this.onIntermissionStart) {
            this.onIntermissionStart(this.intermissionDuration);
        }
        
        // Notify of wave change
        if (this.onWaveChange) {
            this.onWaveChange(this.currentWave, this.isIntermission);
        }
    }
    
    /**
     * Update the wave manager
     * @param {number} deltaTime - Time since last update in seconds
     */
    update(deltaTime) {
        if (this.isIntermission) {
            // Update intermission timer
            this.waveDurationTimer -= deltaTime;
            
            // Start next wave if intermission is over
            if (this.waveDurationTimer <= 0) {
                this.startNextWave();
            }
        }
    }
    
    /**
     * Get current wave number
     * @return {number} Current wave
     */
    getWave() {
        return this.currentWave;
    }
    
    /**
     * Check if a wave is active
     * @return {boolean} True if wave is active, false if in intermission
     */
    isWaveActive() {
        return this.isWaveActive;
    }
    
    /**
     * Get countdown timer for next wave
     * @return {number} Seconds until next wave
     */
    getNextWaveCountdown() {
        return Math.max(0, this.waveDurationTimer);
    }
    
    /**
     * Calculate number of different zombie types for a wave
     * @param {number} wave - Wave number
     * @return {Object} Count of different zombie types
     */
    getZombieCountsForWave(wave) {
        // Calculate base zombies (linear scaling)
        const baseZombies = this.waveConfig.baseZombiesPerWave + 
                            (wave - 1) * this.waveConfig.baseZombieScaling;
        
        // Calculate runner zombies (unlock at specific wave)
        let runners = 0;
        if (wave >= this.waveConfig.runnerUnlockWave) {
            runners = Math.floor((wave - this.waveConfig.runnerUnlockWave + 1) * 
                                this.waveConfig.runnerZombieScaling);
        }
        
        // Calculate boss zombies (unlock at specific wave)
        let bosses = 0;
        if (wave >= this.waveConfig.bossUnlockWave) {
            bosses = Math.ceil((wave - this.waveConfig.bossUnlockWave + 1) * 
                              this.waveConfig.bossZombieScaling);
        }
        
        return {
            base: Math.ceil(baseZombies),
            runners: Math.ceil(runners),
            bosses: Math.ceil(bosses)
        };
    }
    
    /**
     * Calculate total zombies for a wave
     * @param {number} wave - Wave number
     * @return {number} Total zombies in wave
     */
    calculateTotalZombiesForWave(wave) {
        const counts = this.getZombieCountsForWave(wave);
        const requiredZombies = counts.base + counts.runners + counts.bosses;
        // Spawn more zombies than required to kill
        return Math.ceil(requiredZombies * this.waveConfig.spawnMultiplier);
    }
    
    /**
     * Calculate required kills for wave completion
     * @param {number} wave - Wave number
     * @return {number} Required kills to complete wave
     */
    calculateRequiredKillsForWave(wave) {
        const counts = this.getZombieCountsForWave(wave);
        return counts.base + counts.runners + counts.bosses;
    }
    
    /**
     * Calculate max concurrent zombies based on wave
     * @return {number} Max concurrent zombies
     */
    getMaxConcurrentZombies() {
        const maxZombies = this.waveConfig.maxZombiesBase + 
                          (this.currentWave - 1) * this.waveConfig.maxZombiesScaling;
        return Math.min(maxZombies, this.waveConfig.maxZombiesCap);
    }
} 